import React from 'react';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import AppModal from './AppModal';
import { useRemovalRequests } from '@hooks/hr/useRemovalRequests';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    RolesSliderTabWrap,
    ErrorBlock,
    CreateRoleModalBody,
    CreateRoleModalFooter,
} from './style';

const RemovalRequests = () => {
    const {
        total,
        pendingCount,
        tableData,
        columns,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        canView,
        canApproveReject,
        requestsError,
        approveModalOpen,
        closeApproveModal,
        requestToApprove,
        confirmApprove,
        approvePending,
        rejectModalOpen,
        closeRejectModal,
        requestToReject,
        confirmReject,
        rejectPending,
    } = useRemovalRequests();

    if (!canView) {
        return (
            <Container $padding="24px">
                <ErrorBlock>You do not have permission to view removal requests.</ErrorBlock>
            </Container>
        );
    }

    return (
        <ListBodyLayout
            title="Removal Requests"
            description={
                canApproveReject
                    ? 'Review and approve or reject removal requests from HR.'
                    : 'View status of removal requests.'
            }
            kpiCards={[
                {
                    title: 'Total',
                    value: total,
                    change: { value: '', isPositive: true, text: 'Removal requests' },
                },
                {
                    title: 'Pending',
                    value: pendingCount,
                    change: { value: '', isPositive: true, text: 'Awaiting approval' },
                },
                {
                    title: 'Processed',
                    value: total - pendingCount,
                    change: { value: '', isPositive: true, text: 'Approved or rejected' },
                },
            ]}
        >
            {requestsError && (
                <ErrorBlock>
                    Unable to load removal requests. Check that the API (GET /api/v1/users/removal-requests) is
                    available.
                </ErrorBlock>
            )}

            <Container $display="flex" $alignItems="center" $minWidth="0" $marginBottom="16px">
                <RolesSliderTabWrap>
                    <SliderTab
                        items={[
                            { key: STATUS_TAB.ALL, label: 'All' },
                            { key: STATUS_TAB.PENDING, label: 'Pending' },
                            { key: STATUS_TAB.APPROVED, label: 'Approved' },
                            { key: STATUS_TAB.REJECTED, label: 'Rejected' },
                        ]}
                        activeKey={statusTab}
                        onChange={setStatusTab}
                        fitContent
                    />
                </RolesSliderTabWrap>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
            />

            <AppModal
                size="sm"
                title="Approve removal request"
                open={approveModalOpen}
                onCancel={closeApproveModal}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    {requestToApprove && (
                        <p style={{ margin: 0 }}>
                            Approve removal of <strong>{requestToApprove.userName}</strong> (
                            {requestToApprove.userEmail})? The employee will be removed from the company.
                        </p>
                    )}
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} onClick={closeApproveModal} $padding="10px 16px" $size="14px">
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        onClick={confirmApprove}
                        loading={approvePending}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Approve
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="sm"
                title="Reject removal request"
                open={rejectModalOpen}
                onCancel={closeRejectModal}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    {requestToReject && (
                        <p style={{ margin: 0 }}>
                            Reject the removal request for <strong>{requestToReject.userName}</strong> (
                            {requestToReject.userEmail})? The employee will remain in the company.
                        </p>
                    )}
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} onClick={closeRejectModal} $padding="10px 16px" $size="14px">
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmReject}
                        loading={rejectPending}
                        $padding="10px 16px"
                        $size="14px"
                        style={{ backgroundColor: '#f5222d', border: 'none', color: '#fff' }}
                    >
                        Reject
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>
        </ListBodyLayout>
    );
};

export default RemovalRequests;
